/**
 * DECSAL.dta — Declaration mensuelle des salaires
 * Format CCSS SECUline
 *
 * Structure :
 * - Ligne 1 : en-tete employeur (type "E")
 * - Lignes 2..N : detail par salarie (type "S")
 *
 * Champs en-tete (E) :
 *   Type;NumEmployeur;Periode;NbSalaries;TotalBrut;TotalCotSal;TotalCotPat;Version
 *
 * Champs salarie (S) :
 *   Type;Matricule;Nom;Prenom;Periode;JoursTravailles;BrutTotal;BrutPlafonne;
 *   CotMaladieSal;CotPensionSal;CotDependance;TotalCotSal;
 *   CotMaladiePat;CotPensionPat;CotAccident;CotMutualite;CotSante;TotalCotPat;
 *   HeuresSup;MontantHS
 */

import {
  encodeDTA,
  formatPeriodDTA,
  formatMatriculeDTA,
  formatEmployerNumberDTA,
  formatAmountDTA,
  formatDaysDTA,
  toAscii,
} from './dta-utils'

export interface DECSALEmployeeData {
  matriculeCcss: string
  lastName: string
  firstName: string
  workingDays: number
  grossTotalCents: number
  cappedGrossCents: number
  ssEmployeeMaladieCents: number
  ssEmployeePensionCents: number
  ssEmployeeDependanceCents: number
  ssEmployeeTotalCents: number
  ssEmployerMaladieCents: number
  ssEmployerPensionCents: number
  ssEmployerAccidentCents: number
  ssEmployerMutualiteCents: number
  ssEmployerSanteCents: number
  ssEmployerTotalCents: number
  overtimeHours: number
  overtimeCents: number
}

export interface DECSALData {
  employerNumber: string
  periodYear: number
  periodMonth: number
  employees: DECSALEmployeeData[]
}

const DECSAL_VERSION = '01'

export function generateDECSAL(data: DECSALData): string {
  const period = formatPeriodDTA(data.periodYear, data.periodMonth)
  const employerNum = formatEmployerNumberDTA(data.employerNumber)

  const totalBrut = data.employees.reduce((s, e) => s + e.grossTotalCents, 0)
  const totalCotSal = data.employees.reduce((s, e) => s + e.ssEmployeeTotalCents, 0)
  const totalCotPat = data.employees.reduce((s, e) => s + e.ssEmployerTotalCents, 0)

  const lines: string[][] = []

  // Header
  lines.push([
    'E',
    employerNum,
    period,
    String(data.employees.length),
    formatAmountDTA(totalBrut),
    formatAmountDTA(totalCotSal),
    formatAmountDTA(totalCotPat),
    DECSAL_VERSION,
  ])

  // Employee lines
  for (const emp of data.employees) {
    lines.push([
      'S',
      formatMatriculeDTA(emp.matriculeCcss),
      toAscii(emp.lastName),
      toAscii(emp.firstName),
      period,
      formatDaysDTA(emp.workingDays),
      formatAmountDTA(emp.grossTotalCents),
      formatAmountDTA(emp.cappedGrossCents),
      formatAmountDTA(emp.ssEmployeeMaladieCents),
      formatAmountDTA(emp.ssEmployeePensionCents),
      formatAmountDTA(emp.ssEmployeeDependanceCents),
      formatAmountDTA(emp.ssEmployeeTotalCents),
      formatAmountDTA(emp.ssEmployerMaladieCents),
      formatAmountDTA(emp.ssEmployerPensionCents),
      formatAmountDTA(emp.ssEmployerAccidentCents),
      formatAmountDTA(emp.ssEmployerMutualiteCents),
      formatAmountDTA(emp.ssEmployerSanteCents),
      formatAmountDTA(emp.ssEmployerTotalCents),
      String(emp.overtimeHours),
      formatAmountDTA(emp.overtimeCents),
    ])
  }

  return encodeDTA(lines)
}

export function decsalFilename(year: number, month: number): string {
  return `DECSAL_${year}${String(month).padStart(2, '0')}.dta`
}
